import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Upload, RotateCcw, Loader2, Image as ImageIcon } from "lucide-react";
import { IMAGES as DEFAULT_IMAGES } from "../data/restaurant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rivaaz_admin_token";

// Human-friendly labels + where each image shows on the home page.
const SLOTS = [
  { id: "heroPizza", label: "Hero — Butter Chicken Pizza", where: "Main hero, top of home page (large)" },
  { id: "heroCurry", label: "Hero — Curry (secondary)", where: "Used as a fallback hero variant" },
  { id: "naan", label: "Naan / Tandoori", where: "Hero side image + 'Made with love' tile" },
  { id: "margherita", label: "Pizza tile", where: "Three-tile feature row → Pizza card" },
  { id: "curry", label: "Curry tile", where: "Three-tile feature row → Curry card" },
  { id: "tandoor", label: "Tandoor cooking shot", where: "About / craft section" },
  { id: "spices", label: "Indian spices", where: "About section + bottom banner" },
  { id: "biryani", label: "Biryani", where: "Used by featured cards" },
  { id: "butterChicken", label: "Butter chicken close-up", where: "Used by featured cards" },
  { id: "paneer", label: "Paneer dish", where: "Used by featured cards" },
  { id: "pizzaSlice", label: "Pizza slice close-up", where: "Used by featured cards" },
  { id: "wings", label: "Wings / appetizer", where: "Used by featured cards" },
  { id: "rice", label: "Rice / pulao", where: "Used by featured cards" },
  { id: "samosa", label: "Samosa", where: "Used by featured cards" },
  { id: "dessert", label: "Dessert", where: "Used by featured cards" },
];

const MAX_BYTES = 1_500_000; // ~1.5 MB encoded

export default function AdminHome() {
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState({});

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios.get(`${API}/site/home-images`).then((r) => setOverrides(r.data || {})).catch(() => {});
  }, [token, navigate]);

  const headers = { Authorization: `Bearer ${token}` };

  async function fileToDataUrl(file) {
    // Re-encode through a canvas so we strip EXIF and downscale large photos.
    const img = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = r.result;
      };
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const MAX_DIM = 1600;
    const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    // Try a few quality settings to land under MAX_BYTES.
    for (const q of [0.85, 0.75, 0.65, 0.55, 0.45]) {
      const dataUrl = canvas.toDataURL("image/jpeg", q);
      if (dataUrl.length <= MAX_BYTES) return dataUrl;
    }
    return canvas.toDataURL("image/jpeg", 0.4);
  }

  async function handleUpload(slot, file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (JPG or PNG).");
      return;
    }
    setBusy((b) => ({ ...b, [slot]: true }));
    try {
      const dataUrl = await fileToDataUrl(file);
      const { data } = await axios.put(
        `${API}/admin/home-images/${slot}`,
        { image_data: dataUrl },
        { headers }
      );
      setOverrides(data || {});
      toast.success("Image updated. Refresh rivaaz.ca to see it.");
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
        return;
      }
      const msg = err?.response?.data?.detail || "Upload failed — try a smaller image.";
      toast.error(typeof msg === "string" ? msg : msg.message || "Upload failed");
    } finally {
      setBusy((b) => ({ ...b, [slot]: false }));
    }
  }

  async function handleReset(slot) {
    setBusy((b) => ({ ...b, [slot]: true }));
    try {
      const { data } = await axios.put(
        `${API}/admin/home-images/${slot}`,
        { image_data: "" },
        { headers }
      );
      setOverrides(data || {});
      toast.success("Reverted to default.");
    } catch (e) {
      toast.error("Couldn't reset");
    } finally {
      setBusy((b) => ({ ...b, [slot]: false }));
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="admin-home-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center gap-3 mb-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#C9853E]"
            data-testid="admin-home-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to orders
          </Link>
        </div>

        <div className="text-xs tracking-[0.25em] uppercase text-[#C9853E] font-bold mb-2">
          Home page photos
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0E0E1A]">
          Replace any home-page image
        </h1>
        <p className="text-[#57534E] mt-3 max-w-2xl">
          Upload your own photo for any slot. JPGs and PNGs work best. Images are
          automatically resized to fit. Click <strong>Reset</strong> to revert a
          slot to the original photo.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {SLOTS.map((s) => {
            const currentUrl = overrides[s.id] || DEFAULT_IMAGES[s.id];
            const isCustom = Boolean(overrides[s.id]);
            const isBusy = Boolean(busy[s.id]);
            return (
              <div
                key={s.id}
                className="rounded-3xl bg-white border border-[#E7E5E4] overflow-hidden"
                data-testid={`home-slot-${s.id}`}
              >
                <div className="aspect-[16/9] bg-[#FAF8F5] relative">
                  {currentUrl ? (
                    <img
                      src={currentUrl}
                      alt={s.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#57534E]">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                  {isCustom && (
                    <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider bg-[#C9853E] text-white rounded-full px-2 py-1">
                      Custom
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-display text-base font-extrabold text-[#0E0E1A]">
                    {s.label}
                  </div>
                  <div className="text-xs text-[#57534E] mt-1">{s.where}</div>
                  <div className="flex gap-2 mt-4">
                    <label
                      className={`inline-flex items-center gap-2 cursor-pointer text-xs font-bold tracking-wider uppercase rounded-full px-3.5 py-2 transition-all ${
                        isBusy
                          ? "bg-[#E7E5E4] text-[#57534E] cursor-not-allowed"
                          : "bg-[#0E0E1A] text-white hover:bg-[#262533]"
                      }`}
                      data-testid={`home-upload-${s.id}`}
                    >
                      {isBusy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Upload className="w-3.5 h-3.5" />
                      )}
                      {isCustom ? "Replace" : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={isBusy}
                        onChange={(e) => handleUpload(s.id, e.target.files?.[0])}
                      />
                    </label>
                    {isCustom && (
                      <button
                        type="button"
                        onClick={() => handleReset(s.id)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase border border-[#E7E5E4] text-[#57534E] hover:border-[#C9853E] hover:text-[#C9853E] rounded-full px-3.5 py-2 transition-all"
                        data-testid={`home-reset-${s.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
